import type { ReactNode } from 'react';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { NuvoCard } from '@nuvolo/nuux-core/components/NuvoCard';
import { NuvoCheckbox } from '@nuvolo/nuux-core/components/NuvoCheckbox'; // <-- import it
import type ListProps from './ListProps';
import type { GetInfo } from './ListProps';
import { get } from 'lodash';

export type RenderProps<T> = Omit<
  ListProps<T>,
  'data' | 'searchKey' | 'onClick' | 'getItemsQuery'
> & {
  isItemSelected?: boolean;
  selectModeOn?: boolean;
};

const renderItem =
  <T,>({
    status,
    titleKey,
    subtitleKey,
    customChip,
    customHeadRight,
    bodyFields,
    customIcon,
    mobileLayout = false,
    isItemSelected,
    selectModeOn = false,
  }: RenderProps<T>) =>
  (item: T): ReactNode => {
    const getItemValue = (key: GetInfo<T> | ((item: T) => ReactNode) | undefined): ReactNode =>
      typeof key === 'string' ? (get(item, key) as string) : key?.(item);

    return (
      <div className={`nuvo-card-wrapper ${isItemSelected ? 'selected' : 'unselected'}`}>
        <NuvoCard status={status?.(item)}>
          <div className="card-wrapper" style={{ position: 'relative' }}>
            {/* Checkbox in the upper right corner for select mode */}
            {selectModeOn && (
              <div
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 2,
                }}>
                <NuvoCheckbox value={!!isItemSelected} />
              </div>
            )}

            <div className={`card-head${customIcon ? ' with-icon' : ''}`}>
              <div className={customIcon ? 'card-head' : ''}>
                {customIcon && <div className="icon-container">{customIcon(item)}</div>}
                <div className={customIcon && mobileLayout ? 'card-info-desktop' : ''}>
                  <div className="flex gap-12 card-label">
                    <span>{getItemValue(subtitleKey)}</span>
                  </div>
                  <div className="card-value">{getItemValue(titleKey)}</div>
                </div>
              </div>
              {(customChip || customHeadRight) && (
                <div className="card-head-right">{customChip && <div>{customChip(item)}</div>}</div>
              )}
            </div>
            {customIcon && mobileLayout && (
              <div className="card-info-mobile">
                <div className="flex gap-12 card-label">
                  <span>{getItemValue(subtitleKey)}</span>
                  {customHeadRight && <div>{customHeadRight(item)}</div>}
                </div>
                <div className="card-value">{getItemValue(titleKey)}</div>
              </div>
            )}
            <hr className="divider" />
            <div className="card-body">
              {bodyFields?.map((field, index) => {
                const labelClass = field.labelKey || '';
                return (
                  <NuvoBox key={index} id="selector-row">
                    <span className={`card-label ${labelClass}`}>{field.label}</span>
                    <div className={`card-value ${labelClass}`}>
                      {getItemValue(field.key) || '-'}
                    </div>
                  </NuvoBox>
                );
              })}
            </div>
          </div>
        </NuvoCard>
      </div>
    );
  };

export default renderItem;
